import { Routes } from '@angular/router';
import { AuthLayoutComponent } from './shell/auth-layout/auth-layout.component';
import { AuthSelectMethodComponent } from './auth/pages/select-method/select-method.component';
import { activationStepGuard } from './auth/guards/activation-step.guard';
import { loginStepGuard } from './auth/guards/login-step.guard';
import { passwordRecoveryStepGuard } from './auth/guards/password-recovery-step.guard';
import {
  authenticatedGuard,
  redirectIfAuthenticatedGuard,
} from './auth/guards/auth.guard';
import { permissionGuard } from './core/permissions/permission.guard';

export const routes: Routes = [
  {
    path: 'auth',
    canMatch: [redirectIfAuthenticatedGuard],
    component: AuthLayoutComponent,
    children: [
      { path: '', pathMatch: 'full', component: AuthSelectMethodComponent },
      {
        path: 'login',
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'credentials' },
          {
            path: 'credentials',
            loadComponent: () =>
              import('./auth/pages/login/login.component').then(
                m => m.LoginComponent
              ),
          },
          {
            path: 'verify-otp',
            canMatch: [loginStepGuard],
            data: { minStep: 2 },
            loadComponent: () =>
              import('./auth/pages/login/pages/verify-otp/verify-otp.component').then(
                m => m.VerifyLoginOtpComponent
              ),
          },
          {
            path: 'google-auth',
            canMatch: [loginStepGuard],
            data: { minStep: 3 },
            loadComponent: () =>
              import('./auth/pages/login/pages/google-auth/google-auth.component').then(
                m => m.LoginGoogleAuthComponent
              ),
          },
        ],
      },
      {
        path: 'account-activation',
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'verify-username' },
          {
            path: 'verify-username',
            loadComponent: () =>
              import('./auth/pages/account-activation/account-activation.component').then(
                m => m.AccountActivationComponent
              ),
            data: { minStep: 1 },
          },
          {
            path: 'verify-otp',
            canMatch: [activationStepGuard],
            data: { minStep: 2 },
            loadComponent: () =>
              import('./auth/pages/account-activation/pages/verify-otp/verify-otp.component').then(
                m => m.AccountActivationVerifyOtpComponent
              ),
          },
          {
            path: 'set-password',
            canMatch: [activationStepGuard],
            data: { minStep: 3 },
            loadComponent: () =>
              import('./auth/pages/account-activation/pages/set-password/set-password.component').then(
                m => m.SetPasswordComponent
              ),
          },
        ],
      },
      {
        path: 'password-recovery',
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'verify-username' },
          {
            path: 'verify-username',
            loadComponent: () =>
              import('./auth/pages/password-recovery/password-recovery.component').then(
                m => m.PasswordRecoveryComponent
              ),
            data: { minStep: 1 },
          },
          {
            path: 'verify-otp',
            canMatch: [passwordRecoveryStepGuard],
            data: { minStep: 2 },
            loadComponent: () =>
              import('./auth/pages/password-recovery/pages/verify-otp/verify-otp.component').then(
                m => m.PasswordRecoveryVerifyOtpComponent
              ),
          },
          {
            path: 'set-password',
            canMatch: [passwordRecoveryStepGuard],
            data: { minStep: 3 },
            loadComponent: () =>
              import('./auth/pages/password-recovery/pages/set-password/set-password.component').then(
                m => m.SetPasswordComponent
              ),
          },
          {
            path: 'restriction-info',
            canMatch: [passwordRecoveryStepGuard],
            data: { minStep: 4 },
            loadComponent: () =>
              import('./auth/pages/password-recovery/pages/restriction-info/restriction-info.component').then(
                m => m.RestrictionInfoComponent
              ),
          },
        ],
      },
      {
        path: 'initial-setup',
        loadComponent: () =>
          import('./auth/pages/initial-setup/initial-setup.component').then(
            m => m.InitialSetupComponent
          ),
      },
    ],
  },
  {
    path: 'dashboard',
    canMatch: [authenticatedGuard],
    loadComponent: () =>
      import('./shell/layout/layout.component').then(m => m.LayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./dashboard/pages/dashboard.component').then(
            m => m.DashboardComponent
          ),
      },
      {
        path: 'account-settings',
        loadComponent: () =>
          import('./dashboard/pages/account-settings/account-settings.component').then(
            m => m.AccountSettingsComponent
          ),
      },
      {
        path: 'user-access-management',
        canMatch: [permissionGuard],
        data: { permission: 'nav.user-access' },
        loadComponent: () =>
          import('./dashboard/pages/security-settings/user-access-management/user-access-management.component').then(
            m => m.UserAccessManagementComponent
          ),
      },
      {
        path: 'emergency-control',
        canMatch: [permissionGuard],
        data: { permission: 'nav.emergency' },
        loadComponent: () =>
          import('./dashboard/pages/security-settings/emergency-control/emergency-control.component').then(
            m => m.EmergencyControlComponent
          ),
      },
      {
        path: 'user-access-management/add-ip-restriction',
        canMatch: [permissionGuard],
        data: { permission: 'ipRestriction.manage' },
        loadComponent: () =>
          import('./dashboard/pages/security-settings/user-access-management/components/ip-based-login-restrictions/add-or-edit-ip-restriction/add-or-edit-ip-restriction.component').then(
            m => m.AddOrEditIpRestrictionComponent
          ),
      },
      {
        path: 'user-access-management/edit-ip-restriction/:id',
        canMatch: [permissionGuard],
        data: { permission: 'ipRestriction.manage' },
        loadComponent: () =>
          import('./dashboard/pages/security-settings/user-access-management/components/ip-based-login-restrictions/add-or-edit-ip-restriction/add-or-edit-ip-restriction.component').then(
            m => m.AddOrEditIpRestrictionComponent
          ),
      },
      {
        path: 'user-list',
        loadComponent: () =>
          import('./dashboard/pages/users/users.component').then(
            m => m.UserListComponent
          ),
      },
      {
        path: 'user-activity',
        canMatch: [permissionGuard],
        data: { permission: 'nav.user-activity' },
        loadComponent: () =>
          import('./dashboard/pages/user-activity/user-activity.component').then(
            m => m.UserActivityComponent
          ),
      },
      {
        path: 'whitelist-addresses',
        loadComponent: () =>
          import('./dashboard/pages/asset-management/whitelist-addresses/list/whitelist-address-list.component').then(
            m => m.WhitelistAddressListComponent
          ),
      },
      {
        path: 'vaults',
        loadComponent: () =>
          import('./dashboard/pages/asset-management/vaults/list/vault-list.component').then(
            m => m.VaultListComponent
          ),
      },
      {
        path: 'wallets',
        loadComponent: () =>
          import('./dashboard/pages/asset-management/wallets/list/wallet-list.component').then(
            m => m.WalletListComponent
          ),
      },
      {
        path: 'create-wallet',
        canMatch: [permissionGuard],
        data: { permission: 'wallet.create' },
        loadComponent: () =>
          import('./dashboard/pages/asset-management/wallets/add-or-edit/add-wallet/add-wallet.component').then(
            m => m.AddWalletComponent
          ),
      },
      {
        path: 'edit-wallet/:id',
        canMatch: [permissionGuard],
        data: { permission: 'wallet.edit' },
        loadComponent: () =>
          import('./dashboard/pages/asset-management/wallets/add-or-edit/edit-wallet/edit-wallet.component').then(
            m => m.EditWalletComponent
          ),
      },
      {
        path: 'policies',
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/policies/list/policy-list.component').then(
            m => m.PolicyListComponent
          ),
      },
      {
        path: 'create-policy',
        canMatch: [permissionGuard],
        data: { permission: 'policy.create' },
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/policies/create/create-policy.component').then(
            m => m.CreatePolicyComponent
          ),
      },
      {
        path: 'edit-policy/:id',
        canMatch: [permissionGuard],
        data: { permission: 'policy.edit' },
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/policies/edit/edit-policy.component').then(
            m => m.EditPolicyComponent
          ),
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./dashboard/pages/transactions/list/transactions-list.component').then(
            m => m.TransactionsListComponent
          ),
      },
      {
        path: 'create-transaction',
        canMatch: [permissionGuard],
        data: { permission: 'transaction.create' },
        loadComponent: () =>
          import('./dashboard/pages/transactions/create/create-transaction.component').then(
            m => m.CreateTransactionComponent
          ),
      },
      {
        path: 'transactions/:id',
        loadComponent: () =>
          import('./dashboard/pages/transactions/detail/transaction-detail.component').then(
            m => m.TransactionDetailComponent
          ),
      },
      {
        path: 'approvals',
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/approvals/list/approval-list.component').then(
            m => m.ApprovalListComponent
          ),
      },
      {
        path: 'approvals/:id',
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/approvals/details/all-approvals/all-approvals-details.component').then(
            m => m.AllApprovalsDetailsComponent
          ),
      },
      {
        path: 'my-approvals/:id',
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/approvals/details/my-approvals/my-approvals-details.component').then(
            m => m.MyApprovalsDetailsComponent
          ),
      },
      {
        // The standalone "/dashboard/my-requests" list page is gone —
        // the requester audience is now the `?tab=myRequests` view of
        // the approvals list. Redirect any deep link so existing
        // bookmarks (and the sidebar entry) keep working. Use the
        // function form (Angular 18+) so the query string is reliably
        // preserved by the router.
        path: 'my-requests',
        pathMatch: 'full',
        redirectTo: () => '/dashboard/approvals?tab=myRequests',
      },
      {
        path: 'my-requests/:id',
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/approvals/details/my-requests/my-requests-details.component').then(
            m => m.MyRequestsDetailsComponent
          ),
      },
      {
        path: 'policy-detail/:id',
        loadComponent: () =>
          import('./dashboard/pages/laws-and-policies/policies/detail/policy-detail.component').then(
            m => m.PolicyDetailComponent
          ),
      },
      {
        path: 'vaults/:id',
        loadComponent: () =>
          import('./dashboard/pages/asset-management/vaults/detail/vault-detail.component').then(
            m => m.VaultDetailComponent
          ),
      },
      {
        path: 'wallets/:id',
        loadComponent: () =>
          import('./dashboard/pages/asset-management/wallets/detail/wallet-detail.component').then(
            m => m.WalletDetailComponent
          ),
      },
    ],
  },
  { path: '', redirectTo: '/auth', pathMatch: 'full' },
  { path: '**', redirectTo: '/auth' },
];
